import React from "react";
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Tooltip,
  ListSubheader,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import {
  BUILT_IN_MODES,
  SETTING_ACTIONS,
  applyModeAndActions,
  getOperationalActions,
} from "../../config/tradingPresets";

/**
 * Level 1: Mode Preset Selector
 * Top of the scheduler panel — one clear selector for the trading mode preset
 * with summary chips and quick action toggles.
 */
export default function ModePresetSelector({
  settings,
  setSettings,
  selectedModeId,
  setSelectedModeId,
  selectedActions,
  setSelectedActions,
  modeAppliedBanner,
  setModeAppliedBanner,
}) {
  const currentMode = BUILT_IN_MODES.find((m) => m.id === selectedModeId);

  const handleModeChange = (e) => {
    const modeId = e.target.value;
    setSelectedModeId(modeId);
    if (modeId === "custom") {
      setModeAppliedBanner(null);
      return;
    }
    const mode = BUILT_IN_MODES.find((m) => m.id === modeId);
    if (mode) {
      const finalParams = applyModeAndActions(mode.params, selectedActions);
      const newSettings = { ...settings };
      Object.entries(finalParams).forEach(([key, value]) => {
        newSettings[key] = value;
      });
      newSettings.modeId = modeId;
      newSettings.modeName = mode.name;
      newSettings.modeSource = "preset";
      newSettings.activeActions = selectedActions;
      setSettings(newSettings);
      setModeAppliedBanner(mode.name);
      setTimeout(() => setModeAppliedBanner(null), 4000);
    }
  };

  const handleActionToggle = (action) => {
    const isActive = selectedActions.includes(action.id);
    let newActions;
    if (isActive) {
      newActions = selectedActions.filter((id) => id !== action.id);
    } else {
      newActions = [...selectedActions, action.id];
    }
    setSelectedActions(newActions);

    if (selectedModeId !== "custom") {
      const mode = BUILT_IN_MODES.find((m) => m.id === selectedModeId);
      if (mode) {
        const finalParams = applyModeAndActions(mode.params, newActions);
        const newSettings = { ...settings };
        Object.entries(finalParams).forEach(([key, value]) => {
          newSettings[key] = value;
        });
        newSettings.activeActions = newActions;
        setSettings(newSettings);
      }
    } else {
      if (action.actionType === "param_patch" && action.patch && !isActive) {
        const newSettings = { ...settings };
        Object.entries(action.patch).forEach(([key, value]) => {
          newSettings[key] = value;
        });
        newSettings.activeActions = newActions;
        setSettings(newSettings);
      } else {
        setSettings({ ...settings, activeActions: newActions });
      }
    }
  };

  // Summary chips for current mode
  const summaryChips = currentMode
    ? [
        {
          label: `${currentMode.params.leverage || 1}x`,
          color: "primary",
          tooltip: "Leverage",
        },
        {
          label:
            currentMode.params.sidePolicy === "buy_only"
              ? "Buy Only"
              : "Buy + Sell",
          color:
            currentMode.params.sidePolicy === "buy_only" ? "success" : "default",
          tooltip: "Trade Direction",
        },
        {
          label:
            currentMode.intent === "many_small_wins"
              ? "Scalp"
              : currentMode.intent === "fewer_larger_wins"
                ? "Swing"
                : "Selective",
          color: "secondary",
          tooltip: "Hold Intent",
        },
        {
          label: `SL ${currentMode.params.stopLossPercent}%`,
          color: "error",
          tooltip: "Stop Loss",
        },
        {
          label: `TP ${currentMode.params.takeProfitPercent}%`,
          color: "success",
          tooltip: "Take Profit",
        },
      ]
    : [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.default",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <TuneIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight="bold">
          Trading Mode
        </Typography>
        {selectedModeId !== "custom" && currentMode && (
          <Chip
            label={currentMode.shortName}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      {/* Mode Selector */}
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>Mode Preset</InputLabel>
        <Select
          value={selectedModeId}
          onChange={handleModeChange}
          label="Mode Preset"
        >
          <MenuItem value="custom">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>Custom (Manual Settings)</span>
            </Box>
          </MenuItem>
          <ListSubheader
            sx={{
              bgcolor: "background.default",
              fontWeight: "bold",
              fontSize: "0.75rem",
              lineHeight: "32px",
            }}
          >
            ⚡ Turbo Scalp — More trades, small wins, fast exits
          </ListSubheader>
          {BUILT_IN_MODES.filter((m) => m.intent === "many_small_wins").map(
            (mode) => (
              <MenuItem key={mode.id} value={mode.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{mode.icon}</span>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {mode.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {mode.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ),
          )}
          <ListSubheader
            sx={{
              bgcolor: "background.default",
              fontWeight: "bold",
              fontSize: "0.75rem",
              lineHeight: "32px",
            }}
          >
            📈 Trend — Fewer entries, let winners run
          </ListSubheader>
          {BUILT_IN_MODES.filter((m) => m.intent === "fewer_larger_wins").map(
            (mode) => (
              <MenuItem key={mode.id} value={mode.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{mode.icon}</span>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {mode.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {mode.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ),
          )}
          <ListSubheader
            sx={{
              bgcolor: "background.default",
              fontWeight: "bold",
              fontSize: "0.75rem",
              lineHeight: "32px",
            }}
          >
            🧠 Pro Trader Brain — Selective, thesis-based entries
          </ListSubheader>
          {BUILT_IN_MODES.filter((m) => m.intent === "pro_selective").map(
            (mode) => (
              <MenuItem key={mode.id} value={mode.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{mode.icon}</span>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {mode.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {mode.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ),
          )}
        </Select>
      </FormControl>

      {/* Summary Chips */}
      {currentMode && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
          {summaryChips.map((chip, i) => (
            <Tooltip key={i} title={chip.tooltip}>
              <Chip
                label={chip.label}
                size="small"
                color={chip.color}
                variant="outlined"
                sx={{ fontSize: "0.7rem", height: 22 }}
              />
            </Tooltip>
          ))}
        </Box>
      )}

      {/* Mode Description */}
      {selectedModeId !== "custom" && currentMode && (
        <Alert
          severity={
            currentMode.intent === "pro_selective" ? "info" : "success"
          }
          sx={{
            mb: 1.5,
            py: 0.25,
            "& .MuiAlert-message": { fontSize: "0.75rem" },
          }}
          icon={
            currentMode.intent === "pro_selective" ? (
              <span style={{ fontSize: "1.1rem" }}>🧠</span>
            ) : (
              false
            )
          }
        >
          {currentMode.intent === "pro_selective"
            ? "Pro Mode: AI thesis memory active. Fewer trades, stricter gates, pending-preferred entries."
            : currentMode.intent === "many_small_wins"
              ? "Turbo Scalp: High-frequency entries, small TP targets, fast exits. Trailing protects gains."
              : "Trend: Patient entries, wider trailing, let winners run. Rescue available for recovery."}
        </Alert>
      )}

      {/* Mode Applied Banner */}
      {modeAppliedBanner && (
        <Alert
          severity="success"
          sx={{ mb: 1.5, py: 0.5 }}
          onClose={() => setModeAppliedBanner(null)}
        >
          <Typography variant="caption">
            <strong>Preset applied:</strong> {modeAppliedBanner} — all
            parameters updated. Click <strong>Save</strong> to persist.
          </Typography>
        </Alert>
      )}

      {/* Quick Actions */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 0.75, display: "block" }}
      >
        Quick Actions (click to toggle):
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
        {SETTING_ACTIONS.map((action) => {
          const isActive = selectedActions.includes(action.id);
          return (
            <Tooltip key={action.id} title={action.description} arrow>
              <Chip
                label={`${action.icon} ${action.name}`}
                size="small"
                variant={isActive ? "filled" : "outlined"}
                color={isActive ? "primary" : "default"}
                onClick={() => handleActionToggle(action)}
                sx={{
                  cursor: "pointer",
                  fontWeight: isActive ? "bold" : "normal",
                  "&:hover": { opacity: 0.85 },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Active actions summary */}
      {selectedActions.length > 0 && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="caption">
            <strong>{selectedActions.length} action(s) active.</strong>{" "}
            {getOperationalActions(selectedActions).length > 0 && (
              <>
                Operational actions (
                {getOperationalActions(selectedActions)
                  .map((a) => a.name)
                  .join(", ")}
                ) will be applied on save.
              </>
            )}
          </Typography>
        </Alert>
      )}
    </Paper>
  );
}
